import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS, LanguageCode, TRANSLATIONS } from './i18n.translations';

@Injectable({ providedIn: 'root' })
export class I18nService {
  readonly languages = LANGUAGE_OPTIONS;
  private readonly storageKey = 'procurement-language';
  private readonly languageSubject = new BehaviorSubject<LanguageCode>(this.initialLanguage());
  readonly language$ = this.languageSubject.asObservable();

  get language(): LanguageCode {
    return this.languageSubject.value;
  }

  setLanguage(language: LanguageCode): void {
    const next = this.languages.some((item) => item.code === language) ? language : DEFAULT_LANGUAGE;
    localStorage.setItem(this.storageKey, next);
    document.documentElement.lang = next === 'or' ? 'or' : 'en';
    this.languageSubject.next(next);
  }

  t(key: string, params?: Record<string, string | number>): string {
    const value = TRANSLATIONS[this.language][key] || TRANSLATIONS.en[key] || key;
    if (!params) return value;
    return Object.entries(params).reduce(
      (text, [param, replacement]) => text.replace(new RegExp(`\\{${param}\\}`, 'g'), this.localizeValue(replacement)),
      value
    );
  }

  localizeText(value: string | number): string {
    const text = String(value);
    if (this.language !== 'or') return text;
    const digits = ['\u0b66', '\u0b67', '\u0b68', '\u0b69', '\u0b6a', '\u0b6b', '\u0b6c', '\u0b6d', '\u0b6e', '\u0b6f'];
    return text.replace(/\d/g, (digit) => digits[Number(digit)]);
  }

  formatNumber(value: string | number, digitsInfo = '1.0-0'): string {
    const numberValue = Number(value || 0);
    const { minimumFractionDigits, maximumFractionDigits } = this.parseDigitsInfo(digitsInfo);
    const formatted = new Intl.NumberFormat(this.locale, {
      minimumFractionDigits,
      maximumFractionDigits
    }).format(numberValue);
    return this.localizeText(formatted);
  }

  formatCurrency(value: string | number, currency = 'INR', digitsInfo = '1.2-2'): string {
    const numberValue = Number(value || 0);
    const { minimumFractionDigits, maximumFractionDigits } = this.parseDigitsInfo(digitsInfo);
    const formatted = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits
    }).format(numberValue);
    return this.localizeText(formatted);
  }

  formatDate(value: string | number | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return this.localizeText(String(value || ''));
    const formatted = new Intl.DateTimeFormat(this.locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
    return this.localizeText(formatted);
  }

  localizeRecord<T extends Record<string, unknown>>(record: T, baseKey: string): string {
    const suffix = this.language === 'or' ? 'or' : 'en';
    const preferred = record[`${baseKey}_${suffix}`];
    const fallback = record[`${baseKey}_en`] ?? record[baseKey];
    return String(preferred || fallback || '');
  }

  private initialLanguage(): LanguageCode {
    const saved = localStorage.getItem(this.storageKey) as LanguageCode | null;
    const language = this.languages.some((item) => item.code === saved) ? saved! : DEFAULT_LANGUAGE;
    document.documentElement.lang = language === 'or' ? 'or' : 'en';
    return language;
  }

  private get locale(): string {
    return this.language === 'or' ? 'or-IN' : 'en-IN';
  }

  private localizeValue(value: string | number): string {
    return typeof value === 'number' ? this.formatNumber(value) : this.localizeText(value);
  }

  private parseDigitsInfo(digitsInfo: string): { minimumFractionDigits: number; maximumFractionDigits: number } {
    const match = digitsInfo.match(/^\d+\.(\d+)-(\d+)$/);
    if (!match) return { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return {
      minimumFractionDigits: Number(match[1]),
      maximumFractionDigits: Number(match[2])
    };
  }
}
